/**
 * Configuration complète des ressources Azure
 * Définit les champs requis, optionnels et les suggestions pour chaque type de ressource
 */

import {
  AZURE_LOCATIONS,
  AZURE_POPULAR_LOCATIONS,
  VM_SIZES_POPULAR,
  STORAGE_ACCOUNT_TIERS,
  STORAGE_REPLICATION_TYPES,
  STORAGE_ACCESS_TIERS,
  SQL_DATABASE_SKUS,
  APP_SERVICE_SKUS,
  APP_SERVICE_RUNTIME_STACKS,
  CONTAINER_REGISTRY_SKUS,
  KEY_VAULT_SKUS,
  LOAD_BALANCER_SKUS,
  KUBERNETES_VERSIONS,
  OS_TYPES,
  MYSQL_VERSIONS,
  POSTGRESQL_VERSIONS,
  MARIADB_VERSIONS,
  REDIS_CACHE_SKUS,
  COSMOSDB_KINDS,
  COSMOSDB_CONSISTENCY_LEVELS,
  SERVICE_BUS_SKUS,
  EVENT_HUB_SKUS,
  COMMON_VNET_CIDRS,
  COMMON_SUBNET_CIDRS,
  DISK_TYPES,
  DISK_CACHING_OPTIONS,
  DISK_CREATE_OPTIONS,
  DISK_SIZES,
  AVAILABILITY_SET_FAULT_DOMAINS,
  AVAILABILITY_SET_UPDATE_DOMAINS,
  FIREWALL_SKUS,
  VPN_GATEWAY_SKUS,
  VPN_TYPES,
  TRAFFIC_MANAGER_ROUTING_METHODS,
  TRAFFIC_MANAGER_PROTOCOLS,
  COGNITIVE_SERVICES_KINDS,
  COGNITIVE_SERVICES_SKUS,
  MACHINE_LEARNING_SKUS,
  LOGIC_APP_SKUS,
  API_MANAGEMENT_SKUS,
  DATABRICKS_SKUS,
  SYNAPSE_SQL_POOL_SKUS,
  STREAM_ANALYTICS_SKUS,
  STREAM_ANALYTICS_COMPATIBILITY_LEVELS,
  DATA_FACTORY_IR_TYPES,
  CONTAINER_APP_WORKLOAD_PROFILES,
  BATCH_POOL_ALLOCATION_MODES,
  SIGNALR_SERVICE_SKUS,
  SEARCH_SERVICE_SKUS,
  MEDIA_SERVICES_STORAGE_AUTH,
  KUSTO_SKUS,
  IOT_HUB_SKUS,
  DNS_RECORD_TYPES,
  CDN_SKUS,
  FRONTDOOR_SKUS,
  EXPRESS_ROUTE_SKUS,
  EXPRESS_ROUTE_TIERS,
  EXPRESS_ROUTE_FAMILIES,
  EXPRESS_ROUTE_BANDWIDTHS,
  BASTION_HOST_SKUS,
  BOT_SERVICE_SKUS,
  AUTOMATION_ACCOUNT_SKUS,
  IP_CONFIGURATION_VERSIONS,
  RECOVERY_SERVICES_VAULT_SKUS,
  BACKUP_POLICY_FREQUENCY,
  EVENT_GRID_TOPIC_INPUT_SCHEMAS,
  APPLICATION_INSIGHTS_TYPES,
  APP_GATEWAY_SKUS,
  APP_GATEWAY_TIERS,
  APP_GATEWAY_CAPACITY,
  SERVICE_FABRIC_RELIABILITY_LEVELS,
  SERVICE_FABRIC_VM_SIZES,
} from './azureValues';

export interface FieldConfig {
  key: string;
  label: string;
  type: 'text' | 'select' | 'number' | 'boolean' | 'textarea';
  required: boolean;
  default?: any;
  options?: string[];
  suggestions?: string[];
  placeholder?: string;
  description?: string;
  validation?: (value: any) => string | null; // Retourne un message d'erreur ou null
}

export interface ResourceConfig {
  displayName: string;
  category: string;
  description: string;
  fields: FieldConfig[];
  priorityFields?: string[]; // Champs à afficher en priorité (mode simple)
  relationshipSuggestions?: {
    inbound?: string[]; // Quelles ressources peuvent se connecter à celle-ci
    outbound?: string[]; // À quelles ressources celle-ci peut se connecter
  };
}

// Validation functions
const containsVariable = (value: string): boolean => /\{\{[^}]+\}\}/.test(value);

const validateName = (value: string): string | null => {
  if (!value) return "Le nom est requis";
  if (containsVariable(value)) return null;
  if (!/^[a-z0-9]([a-z0-9-]{0,61}[a-z0-9])?$/.test(value)) {
    return "Nom invalide: utiliser lettres minuscules, chiffres et tirets (1-63 caractères)";
  }
  return null;
};

const validateStorageName = (value: string): string | null => {
  if (!value) return "Le nom est requis";
  if (containsVariable(value)) return null;
  if (!/^[a-z0-9]{3,24}$/.test(value)) {
    return "Nom invalide: 3-24 caractères alphanumériques en minuscules uniquement";
  }
  return null;
};

// validateLocation is not currently used but kept for future validation needs
// const validateLocation = (value: string): string | null => {
//   const validLocations = [
//     'eastus', 'westus', 'centralus', 'northeurope', 'westeurope',
//     'southeastasia', 'eastasia', 'australiaeast', 'canadacentral',
//     'uksouth', 'francecentral', 'germanywestcentral', 'switzerlandnorth'
//   ];
//   if (value && !validLocations.includes(value.toLowerCase().replace(/\s/g, ''))) {
//     return "Région Azure non valide";
//   }
//   return null;
// };

export const AZURE_RESOURCE_CONFIG: Record<string, ResourceConfig> = {
  'azurerm_resource_group': {
    displayName: 'Resource Group',
    category: 'Management',
    description: 'Conteneur logique pour regrouper des ressources Azure',
    fields: [
      {
        key: 'name',
        label: 'Nom',
        type: 'text',
        required: true,
        placeholder: 'rg-myapp-prod',
        description: 'Nom unique du resource group',
        suggestions: ['rg-webapp-prod', 'rg-api-dev', 'rg-infrastructure'],
        validation: validateName,
      },
      {
        key: 'location',
        label: 'Région',
        type: 'select',
        required: true,
        default: 'westeurope',
        options: AZURE_LOCATIONS,
        description: 'Région Azure où créer le resource group',
      },
      {
        key: 'tags',
        label: 'Tags',
        type: 'textarea',
        required: false,
        placeholder: '{"environment": "production", "owner": "DevOps"}',
        description: 'Tags pour organiser et gérer les ressources (JSON)',
      },
    ],
    relationshipSuggestions: {
      outbound: ['azurerm_virtual_network', 'azurerm_storage_account', 'azurerm_virtual_machine'],
    },
  },

  'azurerm_virtual_network': {
    displayName: 'Virtual Network',
    category: 'Network',
    description: 'Réseau virtuel Azure isolé et sécurisé',
    fields: [
      {
        key: 'name',
        label: 'Nom',
        type: 'text',
        required: true,
        placeholder: 'vnet-myapp',
        suggestions: ['vnet-webapp-prod', 'vnet-hub', 'vnet-spoke'],
        validation: validateName,
      },
      {
        key: 'location',
        label: 'Région',
        type: 'select',
        required: true,
        default: 'westeurope',
        options: AZURE_LOCATIONS,
      },
      {
        key: 'resource_group_name',
        label: 'Resource Group',
        type: 'text',
        required: true,
        placeholder: 'rg-network',
        description: 'Nom du resource group contenant ce VNet',
      },
      {
        key: 'address_space',
        label: 'Espace d\'adressage',
        type: 'select',
        required: true,
        default: '10.0.0.0/16',
        options: COMMON_VNET_CIDRS,
        description: 'Plage d\'adresses IP CIDR pour le VNet',
      },
    ],
    relationshipSuggestions: {
      outbound: ['azurerm_subnet', 'azurerm_virtual_network_peering'],
      inbound: ['azurerm_resource_group'],
    },
  },

  'azurerm_subnet': {
    displayName: 'Subnet',
    category: 'Network',
    description: 'Sous-réseau à l\'intérieur d\'un Virtual Network',
    fields: [
      {
        key: 'name',
        label: 'Nom',
        type: 'text',
        required: true,
        placeholder: 'subnet-web',
        suggestions: ['subnet-web', 'subnet-app', 'subnet-data', 'subnet-private'],
        validation: validateName,
      },
      {
        key: 'resource_group_name',
        label: 'Resource Group',
        type: 'text',
        required: true,
      },
      {
        key: 'virtual_network_name',
        label: 'Virtual Network',
        type: 'text',
        required: true,
        description: 'Nom du VNet parent',
      },
      {
        key: 'address_prefixes',
        label: 'Préfixes d\'adresses',
        type: 'select',
        required: true,
        default: '10.0.1.0/24',
        options: COMMON_SUBNET_CIDRS,
        description: 'Plage CIDR pour ce subnet (doit être dans le VNet)',
      },
    ],
    relationshipSuggestions: {
      inbound: ['azurerm_virtual_network'],
      outbound: ['azurerm_network_interface', 'azurerm_virtual_machine'],
    },
  },

  'azurerm_storage_account': {
    displayName: 'Storage Account',
    category: 'Storage',
    description: 'Compte de stockage Azure pour blobs, files, queues et tables',
    fields: [
      {
        key: 'name',
        label: 'Nom',
        type: 'text',
        required: true,
        placeholder: 'stmyapp',
        suggestions: ['stwebapp', 'stdata', 'stbackup'],
        validation: validateStorageName,
      },
      {
        key: 'location',
        label: 'Région',
        type: 'select',
        required: true,
        default: 'westeurope',
        options: AZURE_LOCATIONS,
      },
      {
        key: 'resource_group_name',
        label: 'Resource Group',
        type: 'text',
        required: true,
      },
      {
        key: 'account_tier',
        label: 'Tier',
        type: 'select',
        required: true,
        default: 'Standard',
        options: STORAGE_ACCOUNT_TIERS,
        description: 'Niveau de performance du compte',
      },
      {
        key: 'account_replication_type',
        label: 'Réplication',
        type: 'select',
        required: true,
        default: 'LRS',
        options: STORAGE_REPLICATION_TYPES,
        description: 'Type de réplication des données',
      },
      {
        key: 'access_tier',
        label: 'Access Tier',
        type: 'select',
        required: false,
        default: 'Hot',
        options: STORAGE_ACCESS_TIERS,
        description: 'Niveau d\'accès pour Blob Storage',
      },
      {
        key: 'capacity_gb',
        label: 'Capacité estimée (GB)',
        type: 'number',
        required: false,
        default: 100,
        placeholder: '100',
        suggestions: ['100', '500', '1000', '5000'],
        description: 'Capacité estimée pour le calcul des coûts (pay-as-you-go)',
      },
    ],
    relationshipSuggestions: {
      inbound: ['azurerm_resource_group', 'azurerm_virtual_network'],
      outbound: ['azurerm_private_endpoint'],
    },
  },

  'azurerm_virtual_machine': {
    displayName: 'Virtual Machine',
    category: 'Compute',
    description: 'Machine virtuelle Azure pour exécuter des workloads',
    fields: [
      {
        key: 'name',
        label: 'Nom',
        type: 'text',
        required: true,
        placeholder: 'vm-web-01',
        suggestions: ['vm-web-01', 'vm-app-01', 'vm-db-01'],
        validation: validateName,
      },
      {
        key: 'location',
        label: 'Région',
        type: 'select',
        required: true,
        default: 'westeurope',
        options: AZURE_LOCATIONS,
      },
      {
        key: 'resource_group_name',
        label: 'Resource Group',
        type: 'text',
        required: true,
      },
      {
        key: 'vm_size',
        label: 'Taille de VM',
        type: 'select',
        required: true,
        default: 'Standard_B2s',
        options: VM_SIZES_POPULAR,
        description: 'Taille et capacité de la VM',
      },
      {
        key: 'os_type',
        label: 'Type d\'OS',
        type: 'select',
        required: false,
        default: 'Linux',
        options: OS_TYPES,
      },
      {
        key: 'admin_username',
        label: 'Utilisateur admin',
        type: 'text',
        required: false,
        placeholder: 'azureuser',
        suggestions: ['azureuser', 'adminuser'],
      },
    ],
    relationshipSuggestions: {
      inbound: ['azurerm_subnet', 'azurerm_network_interface', 'azurerm_lb'],
      outbound: ['azurerm_managed_disk', 'azurerm_storage_account'],
    },
  },

  'azurerm_app_service': {
    displayName: 'App Service',
    category: 'Web',
    description: 'Service d\'hébergement web géré pour applications',
    fields: [
      {
        key: 'name',
        label: 'Nom',
        type: 'text',
        required: true,
        placeholder: 'app-mywebapp',
        suggestions: ['app-webapp', 'app-api', 'app-frontend'],
        validation: validateName,
      },
      {
        key: 'location',
        label: 'Région',
        type: 'select',
        required: true,
        default: 'westeurope',
        options: AZURE_LOCATIONS,
      },
      {
        key: 'resource_group_name',
        label: 'Resource Group',
        type: 'text',
        required: true,
      },
      {
        key: 'app_service_plan_id',
        label: 'App Service Plan ID',
        type: 'text',
        required: true,
        description: 'ID du plan App Service',
      },
      {
        key: 'runtime_stack',
        label: 'Runtime Stack',
        type: 'select',
        required: false,
        default: 'NODE|18-lts',
        options: APP_SERVICE_RUNTIME_STACKS,
      },
    ],
    relationshipSuggestions: {
      inbound: ['azurerm_app_service_plan', 'azurerm_application_gateway'],
      outbound: ['azurerm_sql_database', 'azurerm_storage_account', 'azurerm_key_vault'],
    },
  },

  'azurerm_sql_database': {
    displayName: 'SQL Database',
    category: 'Database',
    description: 'Base de données SQL gérée',
    fields: [
      {
        key: 'name',
        label: 'Nom',
        type: 'text',
        required: true,
        placeholder: 'sqldb-myapp',
        suggestions: ['sqldb-app', 'sqldb-data', 'sqldb-analytics'],
        validation: validateName,
      },
      {
        key: 'resource_group_name',
        label: 'Resource Group',
        type: 'text',
        required: true,
      },
      {
        key: 'location',
        label: 'Région',
        type: 'select',
        required: true,
        default: 'northeurope',
        options: AZURE_LOCATIONS,
        description: 'Région Azure où déployer la base de données',
      },
      {
        key: 'server_name',
        label: 'SQL Server',
        type: 'text',
        required: true,
        description: 'Nom du serveur SQL parent',
      },
      {
        key: 'sku_name',
        label: 'SKU',
        type: 'select',
        required: false,
        default: 'S0',
        options: SQL_DATABASE_SKUS,
        description: 'Niveau de service et capacité',
      },
      {
        key: 'zone_redundant',
        label: 'Redondance de zone',
        type: 'select',
        required: false,
        default: 'false',
        options: ['true', 'false'],
        description: 'Active la redondance de zone pour haute disponibilité',
      },
    ],
    relationshipSuggestions: {
      inbound: ['azurerm_mssql_server', 'azurerm_app_service', 'azurerm_function_app'],
      outbound: ['azurerm_private_endpoint'],
    },
  },

  'azurerm_lb': {
    displayName: 'Load Balancer',
    category: 'Network',
    description: 'Équilibreur de charge pour distribuer le trafic',
    fields: [
      {
        key: 'name',
        label: 'Nom',
        type: 'text',
        required: true,
        placeholder: 'lb-web',
        suggestions: ['lb-web', 'lb-app', 'lb-internal'],
        validation: validateName,
      },
      {
        key: 'location',
        label: 'Région',
        type: 'select',
        required: true,
        default: 'westeurope',
        options: AZURE_LOCATIONS,
      },
      {
        key: 'resource_group_name',
        label: 'Resource Group',
        type: 'text',
        required: true,
      },
      {
        key: 'sku',
        label: 'SKU',
        type: 'select',
        required: false,
        default: 'Standard',
        options: LOAD_BALANCER_SKUS,
      },
    ],
    relationshipSuggestions: {
      inbound: ['azurerm_public_ip', 'azurerm_virtual_network'],
      outbound: ['azurerm_virtual_machine', 'azurerm_virtual_machine_scale_set'],
    },
  },

  'azurerm_kubernetes_cluster': {
    displayName: 'AKS Cluster',
    category: 'Compute',
    description: 'Cluster Kubernetes managé Azure',
    fields: [
      {
        key: 'name',
        label: 'Nom',
        type: 'text',
        required: true,
        placeholder: 'aks-cluster',
        suggestions: ['aks-prod', 'aks-dev', 'aks-cluster'],
        validation: validateName,
      },
      {
        key: 'location',
        label: 'Région',
        type: 'select',
        required: true,
        default: 'westeurope',
        options: AZURE_LOCATIONS,
      },
      {
        key: 'resource_group_name',
        label: 'Resource Group',
        type: 'text',
        required: true,
      },
      {
        key: 'dns_prefix',
        label: 'DNS Prefix',
        type: 'text',
        required: true,
        placeholder: 'myaks',
        suggestions: ['myaks', 'k8s', 'cluster'],
      },
      {
        key: 'node_count',
        label: 'Nombre de nodes',
        type: 'number',
        required: false,
        default: 3,
      },
      {
        key: 'vm_size',
        label: 'Taille de VM',
        type: 'select',
        required: false,
        default: 'Standard_D2s_v3',
        options: VM_SIZES_POPULAR,
      },
      {
        key: 'kubernetes_version',
        label: 'Version Kubernetes',
        type: 'select',
        required: false,
        default: '1.29',
        options: KUBERNETES_VERSIONS,
        description: 'Version de Kubernetes à utiliser',
      },
    ],
    relationshipSuggestions: {
      inbound: ['azurerm_virtual_network', 'azurerm_container_registry'],
      outbound: ['azurerm_storage_account', 'azurerm_sql_database', 'azurerm_key_vault'],
    },
  },

  'azurerm_key_vault': {
    displayName: 'Key Vault',
    category: 'Security',
    description: 'Coffre-fort pour secrets, clés et certificats',
    fields: [
      {
        key: 'name',
        label: 'Nom',
        type: 'text',
        required: true,
        placeholder: 'kv-myapp',
        suggestions: ['kv-app', 'kv-secrets', 'kv-prod'],
        validation: validateName,
      },
      {
        key: 'location',
        label: 'Région',
        type: 'select',
        required: true,
        default: 'westeurope',
        options: AZURE_LOCATIONS,
      },
      {
        key: 'resource_group_name',
        label: 'Resource Group',
        type: 'text',
        required: true,
      },
      {
        key: 'sku_name',
        label: 'SKU',
        type: 'select',
        required: false,
        default: 'standard',
        options: KEY_VAULT_SKUS,
      },
      {
        key: 'tenant_id',
        label: 'Tenant ID',
        type: 'text',
        required: true,
        description: 'ID du tenant Azure AD',
      },
    ],
    relationshipSuggestions: {
      inbound: ['azurerm_app_service', 'azurerm_function_app', 'azurerm_virtual_machine'],
    },
  },

  'azurerm_container_registry': {
    displayName: 'Container Registry',
    category: 'Containers',
    description: 'Registre privé pour images Docker',
    fields: [
      {
        key: 'name',
        label: 'Nom',
        type: 'text',
        required: true,
        placeholder: 'acrmyapp',
        suggestions: ['acrprod', 'acrdev', 'acrimages'],
        validation: validateStorageName,
      },
      {
        key: 'location',
        label: 'Région',
        type: 'select',
        required: true,
        default: 'westeurope',
        options: AZURE_LOCATIONS,
      },
      {
        key: 'resource_group_name',
        label: 'Resource Group',
        type: 'text',
        required: true,
      },
      {
        key: 'sku',
        label: 'SKU',
        type: 'select',
        required: false,
        default: 'Basic',
        options: CONTAINER_REGISTRY_SKUS,
      },
    ],
    relationshipSuggestions: {
      outbound: ['azurerm_kubernetes_cluster', 'azurerm_container_instance'],
    },
  },

  'azurerm_mysql_server': {
    displayName: 'MySQL Server',
    category: 'Database',
    description: 'Serveur MySQL managé Azure',
    fields: [
      {
        key: 'name',
        label: 'Nom',
        type: 'text',
        required: true,
        placeholder: 'mysql-server',
        suggestions: ['mysql-prod', 'mysql-app', 'mysql-db'],
        validation: validateName,
      },
      {
        key: 'location',
        label: 'Région',
        type: 'select',
        required: true,
        default: 'westeurope',
        options: AZURE_LOCATIONS,
      },
      {
        key: 'resource_group_name',
        label: 'Resource Group',
        type: 'text',
        required: true,
      },
      {
        key: 'version',
        label: 'Version MySQL',
        type: 'select',
        required: true,
        default: '8.0',
        options: MYSQL_VERSIONS,
      },
      {
        key: 'sku_name',
        label: 'SKU',
        type: 'select',
        required: true,
        default: 'B_Gen5_2',
        options: ['B_Gen5_1', 'B_Gen5_2', 'GP_Gen5_2', 'GP_Gen5_4', 'GP_Gen5_8', 'MO_Gen5_2', 'MO_Gen5_4'],
        description: 'Niveau de performance (Basic/General Purpose/Memory Optimized)',
      },
      {
        key: 'storage_mb',
        label: 'Stockage (MB)',
        type: 'select',
        required: true,
        default: '5120',
        options: ['5120', '10240', '51200', '102400', '512000', '1024000'],
        description: 'Capacité de stockage en MB',
      },
      {
        key: 'administrator_login',
        label: 'Login Admin',
        type: 'text',
        required: true,
        placeholder: 'mysqladmin',
      },
      {
        key: 'geo_redundant_backup_enabled',
        label: 'Sauvegarde géo-redondante',
        type: 'select',
        required: false,
        default: 'false',
        options: ['true', 'false'],
        description: 'Active la sauvegarde géo-redondante (réplication inter-régions)',
      },
    ],
    relationshipSuggestions: {
      inbound: ['azurerm_app_service', 'azurerm_virtual_machine'],
      outbound: ['azurerm_private_endpoint'],
    },
  },

  'azurerm_postgresql_server': {
    displayName: 'PostgreSQL Server',
    category: 'Database',
    description: 'Serveur PostgreSQL managé Azure',
    fields: [
      {
        key: 'name',
        label: 'Nom',
        type: 'text',
        required: true,
        placeholder: 'psql-server',
        suggestions: ['psql-prod', 'psql-app', 'psql-db'],
        validation: validateName,
      },
      {
        key: 'location',
        label: 'Région',
        type: 'select',
        required: true,
        default: 'westeurope',
        options: AZURE_LOCATIONS,
      },
      {
        key: 'resource_group_name',
        label: 'Resource Group',
        type: 'text',
        required: true,
      },
      {
        key: 'version',
        label: 'Version PostgreSQL',
        type: 'select',
        required: true,
        default: '16',
        options: POSTGRESQL_VERSIONS,
      },
      {
        key: 'sku_name',
        label: 'SKU',
        type: 'select',
        required: true,
        default: 'B_Gen5_2',
        options: ['B_Gen5_1', 'B_Gen5_2', 'GP_Gen5_2', 'GP_Gen5_4', 'GP_Gen5_8', 'MO_Gen5_2', 'MO_Gen5_4'],
        description: 'Niveau de performance',
      },
      {
        key: 'storage_mb',
        label: 'Stockage (MB)',
        type: 'select',
        required: true,
        default: '5120',
        options: ['5120', '10240', '51200', '102400', '512000', '1024000'],
      },
      {
        key: 'administrator_login',
        label: 'Login Admin',
        type: 'text',
        required: true,
        placeholder: 'psqladmin',
      },
      {
        key: 'geo_redundant_backup_enabled',
        label: 'Sauvegarde géo-redondante',
        type: 'select',
        required: false,
        default: 'false',
        options: ['true', 'false'],
        description: 'Active la sauvegarde géo-redondante (réplication inter-régions)',
      },
    ],
    relationshipSuggestions: {
      inbound: ['azurerm_app_service', 'azurerm_virtual_machine'],
      outbound: ['azurerm_private_endpoint'],
    },
  },

  'azurerm_redis_cache': {
    displayName: 'Redis Cache',
    category: 'Database',
    description: 'Cache Redis managé pour améliorer les performances',
    fields: [
      {
        key: 'name',
        label: 'Nom',
        type: 'text',
        required: true,
        placeholder: 'redis-cache',
        suggestions: ['redis-app', 'redis-session', 'redis-cache'],
        validation: validateName,
      },
      {
        key: 'location',
        label: 'Région',
        type: 'select',
        required: true,
        default: 'westeurope',
        options: AZURE_LOCATIONS,
      },
      {
        key: 'resource_group_name',
        label: 'Resource Group',
        type: 'text',
        required: true,
      },
      {
        key: 'sku_name',
        label: 'SKU',
        type: 'select',
        required: true,
        default: 'Standard',
        options: REDIS_CACHE_SKUS,
      },
      {
        key: 'family',
        label: 'Famille',
        type: 'select',
        required: true,
        default: 'C',
        options: ['C', 'P'],
        description: 'C pour Basic/Standard, P pour Premium',
      },
      {
        key: 'capacity',
        label: 'Capacité',
        type: 'select',
        required: true,
        default: '1',
        options: ['0', '1', '2', '3', '4', '5', '6'],
        description: 'Taille du cache (0-6)',
      },
      {
        key: 'zones',
        label: 'Zones de disponibilité',
        type: 'select',
        required: false,
        default: 'none',
        options: ['none', '1', '2', '3', '1,2', '1,3', '2,3', '1,2,3'],
        description: 'Zones de disponibilité pour haute disponibilité (Premium uniquement)',
      },
    ],
    relationshipSuggestions: {
      inbound: ['azurerm_app_service', 'azurerm_function_app'],
      outbound: ['azurerm_private_endpoint'],
    },
  },

  'azurerm_cosmosdb_account': {
    displayName: 'Cosmos DB Account',
    category: 'Database',
    description: 'Base de données NoSQL distribuée globalement',
    fields: [
      {
        key: 'name',
        label: 'Nom',
        type: 'text',
        required: true,
        placeholder: 'cosmos-account',
        suggestions: ['cosmos-app', 'cosmos-db', 'cosmos-global'],
        validation: validateName,
      },
      {
        key: 'location',
        label: 'Région',
        type: 'select',
        required: true,
        default: 'westeurope',
        options: AZURE_LOCATIONS,
      },
      {
        key: 'resource_group_name',
        label: 'Resource Group',
        type: 'text',
        required: true,
      },
      {
        key: 'kind',
        label: 'Type d\'API',
        type: 'select',
        required: false,
        default: 'GlobalDocumentDB',
        options: COSMOSDB_KINDS,
        description: 'API Cosmos DB (SQL, MongoDB, Cassandra, etc.)',
      },
      {
        key: 'consistency_policy_level',
        label: 'Niveau de cohérence',
        type: 'select',
        required: false,
        default: 'Session',
        options: COSMOSDB_CONSISTENCY_LEVELS,
      },
      {
        key: 'offer_type',
        label: 'Type d\'offre',
        type: 'select',
        required: true,
        default: 'Standard',
        options: ['Standard'],
      },
      {
        key: 'enable_automatic_failover',
        label: 'Basculement automatique',
        type: 'select',
        required: false,
        default: 'false',
        options: ['true', 'false'],
        description: 'Active le basculement automatique entre régions',
      },
      {
        key: 'enable_multiple_write_locations',
        label: 'Multi-région écritures',
        type: 'select',
        required: false,
        default: 'false',
        options: ['true', 'false'],
        description: 'Active les écritures dans plusieurs régions (geo-redondance)',
      },
    ],
    relationshipSuggestions: {
      inbound: ['azurerm_app_service', 'azurerm_function_app'],
    },
  },

  'azurerm_function_app': {
    displayName: 'Function App',
    category: 'Compute',
    description: 'Application serverless pour exécuter du code événementiel',
    fields: [
      {
        key: 'name',
        label: 'Nom',
        type: 'text',
        required: true,
        placeholder: 'func-myapp',
        suggestions: ['func-api', 'func-processor', 'func-webhook'],
        validation: validateName,
      },
      {
        key: 'location',
        label: 'Région',
        type: 'select',
        required: true,
        default: 'westeurope',
        options: AZURE_LOCATIONS,
      },
      {
        key: 'resource_group_name',
        label: 'Resource Group',
        type: 'text',
        required: true,
      },
      {
        key: 'app_service_plan_id',
        label: 'App Service Plan ID',
        type: 'text',
        required: true,
      },
      {
        key: 'storage_account_name',
        label: 'Storage Account',
        type: 'text',
        required: true,
        description: 'Compte de stockage pour la fonction',
      },
      {
        key: 'runtime',
        label: 'Runtime',
        type: 'select',
        required: false,
        default: 'node',
        options: ['node', 'dotnet', 'python', 'java', 'powershell'],
      },
    ],
    relationshipSuggestions: {
      inbound: ['azurerm_storage_account', 'azurerm_app_service_plan'],
      outbound: ['azurerm_cosmosdb_account', 'azurerm_storage_account', 'azurerm_sql_database'],
    },
  },

  'azurerm_app_service_plan': {
    displayName: 'App Service Plan',
    category: 'Compute',
    description: 'Plan d\'hébergement pour App Services',
    fields: [
      {
        key: 'name',
        label: 'Nom',
        type: 'text',
        required: true,
        placeholder: 'plan-myapp',
        suggestions: ['plan-prod', 'plan-dev', 'plan-web'],
        validation: validateName,
      },
      {
        key: 'location',
        label: 'Région',
        type: 'select',
        required: true,
        default: 'westeurope',
        options: AZURE_LOCATIONS,
      },
      {
        key: 'resource_group_name',
        label: 'Resource Group',
        type: 'text',
        required: true,
      },
      {
        key: 'sku_name',
        label: 'SKU',
        type: 'select',
        required: true,
        default: 'B1',
        options: APP_SERVICE_SKUS,
        description: 'Niveau de tarification et capacité',
      },
      {
        key: 'os_type',
        label: 'Type d\'OS',
        type: 'select',
        required: false,
        default: 'Linux',
        options: OS_TYPES,
      },
    ],
    relationshipSuggestions: {
      outbound: ['azurerm_app_service', 'azurerm_function_app'],
    },
  },

  'azurerm_service_bus_namespace': {
    displayName: 'Service Bus Namespace',
    category: 'Integration',
    description: 'Messaging cloud pour intégration d\'applications',
    fields: [
      {
        key: 'name',
        label: 'Nom',
        type: 'text',
        required: true,
        placeholder: 'sb-namespace',
        suggestions: ['sb-messaging', 'sb-events', 'sb-queue'],
        validation: validateName,
      },
      {
        key: 'location',
        label: 'Région',
        type: 'select',
        required: true,
        default: 'westeurope',
        options: AZURE_LOCATIONS,
      },
      {
        key: 'resource_group_name',
        label: 'Resource Group',
        type: 'text',
        required: true,
      },
      {
        key: 'sku',
        label: 'SKU',
        type: 'select',
        required: true,
        default: 'Standard',
        options: SERVICE_BUS_SKUS,
      },
    ],
    relationshipSuggestions: {
      inbound: ['azurerm_app_service', 'azurerm_function_app'],
      outbound: ['azurerm_app_service', 'azurerm_function_app'],
    },
  },

  'azurerm_eventhub_namespace': {
    displayName: 'Event Hub Namespace',
    category: 'Analytics',
    description: 'Service d\'ingestion d\'événements à grande échelle',
    fields: [
      {
        key: 'name',
        label: 'Nom',
        type: 'text',
        required: true,
        placeholder: 'evh-namespace',
        suggestions: ['evh-events', 'evh-telemetry', 'evh-logs'],
        validation: validateName,
      },
      {
        key: 'location',
        label: 'Région',
        type: 'select',
        required: true,
        default: 'westeurope',
        options: AZURE_LOCATIONS,
      },
      {
        key: 'resource_group_name',
        label: 'Resource Group',
        type: 'text',
        required: true,
      },
      {
        key: 'sku',
        label: 'SKU',
        type: 'select',
        required: true,
        default: 'Standard',
        options: EVENT_HUB_SKUS,
      },
      {
        key: 'capacity',
        label: 'Capacité (Throughput Units)',
        type: 'select',
        required: false,
        default: '1',
        options: ['1', '2', '4', '8', '12', '16', '20'],
      },
    ],
    relationshipSuggestions: {
      inbound: ['azurerm_app_service', 'azurerm_function_app', 'azurerm_iot_hub'],
      outbound: ['azurerm_stream_analytics_job', 'azurerm_function_app'],
    },
  },

  'azurerm_public_ip': {
    displayName: 'Public IP',
    category: 'Network',
    description: 'Adresse IP publique',
    fields: [
      {
        key: 'name',
        label: 'Nom',
        type: 'text',
        required: true,
        placeholder: 'pip-resource',
        suggestions: ['pip-lb', 'pip-vm', 'pip-gateway'],
        validation: validateName,
      },
      {
        key: 'location',
        label: 'Région',
        type: 'select',
        required: true,
        default: 'westeurope',
        options: AZURE_LOCATIONS,
      },
      {
        key: 'resource_group_name',
        label: 'Resource Group',
        type: 'text',
        required: true,
      },
      {
        key: 'allocation_method',
        label: 'Méthode d\'allocation',
        type: 'select',
        required: true,
        default: 'Static',
        options: ['Static', 'Dynamic'],
      },
      {
        key: 'sku',
        label: 'SKU',
        type: 'select',
        required: false,
        default: 'Standard',
        options: ['Basic', 'Standard'],
      },
    ],
    relationshipSuggestions: {
      outbound: ['azurerm_lb', 'azurerm_virtual_machine', 'azurerm_application_gateway'],
    },
  },

  'azurerm_network_security_group': {
    displayName: 'Network Security Group',
    category: 'Network',
    description: 'Groupe de sécurité réseau pour filtrer le trafic',
    fields: [
      {
        key: 'name',
        label: 'Nom',
        type: 'text',
        required: true,
        placeholder: 'nsg-subnet',
        suggestions: ['nsg-web', 'nsg-app', 'nsg-db'],
        validation: validateName,
      },
      {
        key: 'location',
        label: 'Région',
        type: 'select',
        required: true,
        default: 'westeurope',
        options: AZURE_LOCATIONS,
      },
      {
        key: 'resource_group_name',
        label: 'Resource Group',
        type: 'text',
        required: true,
      },
    ],
    relationshipSuggestions: {
      outbound: ['azurerm_subnet', 'azurerm_network_interface'],
    },
  },

  'azurerm_private_endpoint': {
    displayName: 'Private Endpoint',
    category: 'Network',
    description: 'Point de terminaison privé pour connexion sécurisée',
    fields: [
      {
        key: 'name',
        label: 'Nom',
        type: 'text',
        required: true,
        placeholder: 'pe-resource',
        suggestions: ['pe-storage', 'pe-sql', 'pe-keyvault'],
        validation: validateName,
      },
      {
        key: 'location',
        label: 'Région',
        type: 'select',
        required: true,
        default: 'westeurope',
        options: AZURE_LOCATIONS,
      },
      {
        key: 'resource_group_name',
        label: 'Resource Group',
        type: 'text',
        required: true,
      },
      {
        key: 'subnet_id',
        label: 'Subnet ID',
        type: 'text',
        required: true,
        description: 'ID du subnet pour le private endpoint',
      },
    ],
    relationshipSuggestions: {
      inbound: ['azurerm_subnet'],
      outbound: ['azurerm_storage_account', 'azurerm_sql_database', 'azurerm_key_vault'],
    },
  },
  'azurerm_linux_virtual_machine': {
  displayName: 'Linux Virtual Machine',
  category: 'Compute',
  description: 'Machine virtuelle Linux',
  fields: [
    {
      key: 'name',
      label: 'Nom',
      type: 'text',
      required: true,
      placeholder: 'vm-linux-prod',
    },
    {
      key: 'resource_group_name',
      label: 'Resource Group',
      type: 'text',
      required: true,
    },
    {
      key: 'size',
      label: 'VM Size',
      type: 'select',
      required: true,
      default: 'Standard_D2s_v3',
      options: VM_SIZES_POPULAR,
    },
    {
      key: 'admin_username',
      label: 'Admin Username',
      type: 'text',
      required: true,
      default: 'azureuser',
    },
  ],
  relationshipSuggestions: {
    inbound: ['azurerm_virtual_network', 'azurerm_subnet', 'azurerm_network_interface'],
    outbound: ['azurerm_managed_disk', 'azurerm_storage_account'],
  },
},

'azurerm_orchestrated_virtual_machine_scale_set': {
  displayName: 'VM Scale Set',
  category: 'Compute',
  description: 'Ensemble de machines virtuelles avec mise à l\'échelle automatique',
  fields: [
    {
      key: 'name',
      label: 'Nom',
      type: 'text',
      required: true,
      placeholder: 'vmss-webapp',
    },
    {
      key: 'resource_group_name',
      label: 'Resource Group',
      type: 'text',
      required: true,
    },
    {
      key: 'sku_name',
      label: 'SKU',
      type: 'select',
      required: false,
      default: 'Standard_D2s_v3',
      options: VM_SIZES_POPULAR,
    },
  ],
  relationshipSuggestions: {
    inbound: ['azurerm_lb', 'azurerm_virtual_network'],
    outbound: ['azurerm_storage_account'],
  },
},

'azurerm_availability_set': {
  displayName: 'Availability Set',
  category: 'Compute',
  description: 'Groupe de disponibilité pour les VMs',
  fields: [
    {
      key: 'name',
      label: 'Nom',
      type: 'text',
      required: true,
      placeholder: 'avset-prod',
    },
    {
      key: 'resource_group_name',
      label: 'Resource Group',
      type: 'text',
      required: true,
    },
    {
      key: 'platform_fault_domain_count',
      label: 'Fault Domains',
      type: 'select',
      required: false,
      default: 2,
      options: AVAILABILITY_SET_FAULT_DOMAINS.map(String),
    },
    {
      key: 'platform_update_domain_count',
      label: 'Update Domains',
      type: 'select',
      required: false,
      default: 5,
      options: AVAILABILITY_SET_UPDATE_DOMAINS.map(String),
    },
  ],
  relationshipSuggestions: {
    inbound: [],
    outbound: ['azurerm_virtual_machine', 'azurerm_linux_virtual_machine'],
  },
},

'azurerm_container_group': {
  displayName: 'Container Instance',
  category: 'Compute',
  description: 'Container Instance pour exécuter des conteneurs',
  fields: [
    {
      key: 'name',
      label: 'Nom',
      type: 'text',
      required: true,
      placeholder: 'aci-myapp',
    },
    {
      key: 'resource_group_name',
      label: 'Resource Group',
      type: 'text',
      required: true,
    },
    {
      key: 'os_type',
      label: 'OS Type',
      type: 'select',
      required: true,
      default: 'Linux',
      options: OS_TYPES,
    },
  ],
  relationshipSuggestions: {
    inbound: ['azurerm_virtual_network', 'azurerm_subnet'],
    outbound: ['azurerm_storage_account', 'azurerm_container_registry'],
  },
},

'azurerm_container_app': {
  displayName: 'Container App',
  category: 'Compute',
  description: 'Application conteneurisée serverless',
  fields: [
    {
      key: 'name',
      label: 'Nom',
      type: 'text',
      required: true,
      placeholder: 'ca-myapp',
    },
    {
      key: 'resource_group_name',
      label: 'Resource Group',
      type: 'text',
      required: true,
    },
    {
      key: 'container_app_environment_id',
      label: 'Environment ID',
      type: 'text',
      required: true,
    },
  ],
  relationshipSuggestions: {
    inbound: [],
    outbound: ['azurerm_container_registry', 'azurerm_storage_account'],
  },
},

'azurerm_batch_account': {
  displayName: 'Batch Account',
  category: 'Compute',
  description: 'Compte Batch pour le calcul parallèle à grande échelle',
  fields: [
    {
      key: 'name',
      label: 'Nom',
      type: 'text',
      required: true,
      placeholder: 'batch-compute',
    },
    {
      key: 'resource_group_name',
      label: 'Resource Group',
      type: 'text',
      required: true,
    },
    {
      key: 'pool_allocation_mode',
      label: 'Pool Allocation Mode',
      type: 'select',
      required: false,
      default: 'BatchService',
      options: BATCH_POOL_ALLOCATION_MODES,
    },
  ],
  relationshipSuggestions: {
    inbound: [],
    outbound: ['azurerm_storage_account'],
  },
},

// ==================== DATABASE ====================

'azurerm_mssql_server': {
  displayName: 'SQL Server',
  category: 'Database',
  description: 'Serveur SQL Azure',
  fields: [
    {
      key: 'name',
      label: 'Nom',
      type: 'text',
      required: true,
      placeholder: 'sqlserver-prod',
    },
    {
      key: 'resource_group_name',
      label: 'Resource Group',
      type: 'text',
      required: true,
    },
    {
      key: 'location',
      label: 'Région',
      type: 'select',
      required: true,
      default: 'westeurope',
      options: AZURE_POPULAR_LOCATIONS,
    },
    {
      key: 'version',
      label: 'Version',
      type: 'select',
      required: true,
      default: '12.0',
      options: ['12.0', '2.0'],
    },
    {
      key: 'administrator_login',
      label: 'Admin Login',
      type: 'text',
      required: true,
      default: 'sqladmin',
    },
    {
      key: 'minimum_tls_version',
      label: 'TLS Version minimale',
      type: 'select',
      required: false,
      default: '1.2',
      options: ['1.0', '1.1', '1.2', '1.3'],
    },
    {
      key: 'public_network_access_enabled',
      label: 'Accès réseau public',
      type: 'select',
      required: false,
      default: 'true',
      options: ['true', 'false'],
    },
  ],
  relationshipSuggestions: {
    inbound: [],
    outbound: ['azurerm_mssql_database', 'azurerm_private_endpoint'],
  },
},

'azurerm_mssql_database': {
  displayName: 'SQL Database (MSSQL)',
  category: 'Database',
  description: 'Base de données SQL Azure (nouveau format)',
  fields: [
    {
      key: 'name',
      label: 'Nom',
      type: 'text',
      required: true,
      placeholder: 'sqldb-data',
    },
    {
      key: 'server_id',
      label: 'SQL Server ID',
      type: 'text',
      required: true,
    },
    {
      key: 'location',
      label: 'Région',
      type: 'select',
      required: true,
      default: 'northeurope',
      options: AZURE_POPULAR_LOCATIONS,
      description: 'Région Azure où déployer la base de données',
    },
    {
      key: 'sku_name',
      label: 'SKU',
      type: 'select',
      required: false,
      default: 'S0',
      options: SQL_DATABASE_SKUS,
      description: 'GP_S_Gen5_2 pour serverless, S0-S12 pour standard, P1-P15 pour premium',
    },
    {
      key: 'max_size_gb',
      label: 'Taille maximale (GB)',
      type: 'select',
      required: false,
      default: 32,
      options: ['1', '2', '5', '10', '20', '32', '50', '100', '150', '200', '250', '500', '1024', '2048', '4096'],
      description: 'Taille maximale de la base de données en GB',
    },
    {
      key: 'collation',
      label: 'Collation',
      type: 'select',
      required: false,
      default: 'SQL_Latin1_General_CP1_CI_AS',
      options: [
        'SQL_Latin1_General_CP1_CI_AS',
        'SQL_Latin1_General_CP1_CS_AS',
        'Latin1_General_100_CI_AS',
        'Latin1_General_100_CS_AS',
        'French_CI_AS',
        'French_CS_AS',
      ],
    },
    {
      key: 'auto_pause_delay_in_minutes',
      label: 'Délai auto-pause (minutes)',
      type: 'select',
      required: false,
      options: ['-1', '60', '120', '180', '240', '360', '720', '1440'],
      description: 'Pour serverless uniquement. -1 = désactivé, 60-1440 = minutes avant pause',
    },
    {
      key: 'min_capacity',
      label: 'Capacité min (vCores)',
      type: 'select',
      required: false,
      options: ['0.5', '0.75', '1', '1.25', '1.5', '1.75', '2', '2.5', '3', '4'],
      description: 'Pour serverless uniquement. Capacité minimum en vCores',
    },
    {
      key: 'zone_redundant',
      label: 'Redondance de zone',
      type: 'select',
      required: false,
      default: 'false',
      options: ['true', 'false'],
    },
    {
      key: 'read_scale',
      label: 'Read Scale-Out',
      type: 'select',
      required: false,
      default: 'false',
      options: ['true', 'false'],
      description: 'Active les réplicas en lecture',
    },
    {
      key: 'read_replica_count',
      label: 'Nombre de replicas en lecture',
      type: 'select',
      required: false,
      options: ['0', '1', '2', '3', '4'],
      description: 'Nombre de replicas en lecture seule',
    },
    {
      key: 'license_type',
      label: 'Type de licence',
      type: 'select',
      required: false,
      options: ['LicenseIncluded', 'BasePrice'],
      description: 'LicenseIncluded ou BasePrice (pour Azure Hybrid Benefit)',
    },
    {
      key: 'sample_name',
      label: 'Base d\'exemple',
      type: 'select',
      required: false,
      options: ['', 'AdventureWorksLT'],
      description: 'Crée une base d\'exemple (AdventureWorksLT)',
    },
  ],
  relationshipSuggestions: {
    inbound: ['azurerm_mssql_server'],
    outbound: ['azurerm_private_endpoint'],
  },
},

'azurerm_mssql_managed_instance': {
  displayName: 'SQL Managed Instance',
  category: 'Database',
  description: 'Instance SQL gérée avec compatibilité complète SQL Server',
  fields: [
    {
      key: 'name',
      label: 'Nom',
      type: 'text',
      required: true,
      placeholder: 'sqlmi-prod',
    },
    {
      key: 'resource_group_name',
      label: 'Resource Group',
      type: 'text',
      required: true,
    },
    {
      key: 'sku_name',
      label: 'SKU',
      type: 'select',
      required: true,
      default: 'GP_Gen5',
      options: ['GP_Gen5', 'BC_Gen5'],
    },
    {
      key: 'vcores',
      label: 'vCores',
      type: 'select',
      required: true,
      default: 4,
      options: ['4', '8', '16', '24', '32', '40', '64', '80'],
    },
  ],
  relationshipSuggestions: {
    inbound: ['azurerm_subnet'],
    outbound: ['azurerm_storage_account'],
  },
},

'azurerm_mariadb_server': {
  displayName: 'MariaDB Server',
  category: 'Database',
  description: 'Serveur MariaDB géré',
  fields: [
    {
      key: 'name',
      label: 'Nom',
      type: 'text',
      required: true,
      placeholder: 'mariadb-prod',
    },
    {
      key: 'resource_group_name',
      label: 'Resource Group',
      type: 'text',
      required: true,
    },
    {
      key: 'sku_name',
      label: 'SKU',
      type: 'select',
      required: true,
      default: 'B_Gen5_1',
      options: ['B_Gen5_1', 'B_Gen5_2', 'GP_Gen5_2', 'GP_Gen5_4', 'GP_Gen5_8', 'MO_Gen5_2', 'MO_Gen5_4'],
    },
    {
      key: 'version',
      label: 'Version',
      type: 'select',
      required: true,
      default: '10.3',
      options: MARIADB_VERSIONS,
    },
  ],
  relationshipSuggestions: {
    inbound: [],
    outbound: ['azurerm_private_endpoint'],
  },
},

// ==================== NETWORK ====================

'azurerm_firewall': {
  displayName: 'Firewall',
  category: 'Network',
  description: 'Pare-feu Azure pour sécuriser les réseaux',
  fields: [
    {
      key: 'name',
      label: 'Nom',
      type: 'text',
      required: true,
      placeholder: 'fw-prod',
    },
    {
      key: 'resource_group_name',
      label: 'Resource Group',
      type: 'text',
      required: true,
    },
    {
      key: 'sku_name',
      label: 'SKU Name',
      type: 'select',
      required: true,
      default: 'AZFW_VNet',
      options: FIREWALL_SKUS,
    },
    {
      key: 'sku_tier',
      label: 'SKU Tier',
      type: 'select',
      required: true,
      default: 'Standard',
      options: ['Standard', 'Premium', 'Basic'],
    },
  ],
  relationshipSuggestions: {
    inbound: ['azurerm_virtual_network', 'azurerm_subnet'],
    outbound: ['azurerm_public_ip'],
  },
},

'azurerm_vpn_gateway': {
  displayName: 'VPN Gateway',
  category: 'Network',
  description: 'Passerelle VPN pour connexions sécurisées',
  fields: [
    {
      key: 'name',
      label: 'Nom',
      type: 'text',
      required: true,
      placeholder: 'vng-onprem',
    },
    {
      key: 'resource_group_name',
      label: 'Resource Group',
      type: 'text',
      required: true,
    },
    {
      key: 'type',
      label: 'VPN Type',
      type: 'select',
      required: true,
      default: 'RouteBased',
      options: VPN_TYPES,
    },
    {
      key: 'sku',
      label: 'SKU',
      type: 'select',
      required: true,
      default: 'VpnGw1',
      options: VPN_GATEWAY_SKUS,
    },
  ],
  relationshipSuggestions: {
    inbound: ['azurerm_virtual_network', 'azurerm_subnet'],
    outbound: ['azurerm_public_ip'],
  },
},

'azurerm_traffic_manager_profile': {
  displayName: 'Traffic Manager',
  category: 'Network',
  description: 'Gestionnaire de trafic DNS pour la distribution globale',
  fields: [
    {
      key: 'name',
      label: 'Nom',
      type: 'text',
      required: true,
      placeholder: 'tm-global',
    },
    {
      key: 'resource_group_name',
      label: 'Resource Group',
      type: 'text',
      required: true,
    },
    {
      key: 'traffic_routing_method',
      label: 'Routing Method',
      type: 'select',
      required: true,
      default: 'Performance',
      options: TRAFFIC_MANAGER_ROUTING_METHODS,
    },
    {
      key: 'protocol',
      label: 'Protocol',
      type: 'select',
      required: false,
      default: 'HTTP',
      options: TRAFFIC_MANAGER_PROTOCOLS,
    },
  ],
  relationshipSuggestions: {
    inbound: [],
    outbound: ['azurerm_public_ip', 'azurerm_app_service'],
  },
},

'azurerm_application_gateway': {
  displayName: 'Application Gateway',
  category: 'Network',
  description: 'Équilibreur de charge de couche 7 avec WAF',
  fields: [
    {
      key: 'name',
      label: 'Nom',
      type: 'text',
      required: true,
      placeholder: 'agw-webapp',
    },
    {
      key: 'resource_group_name',
      label: 'Resource Group',
      type: 'text',
      required: true,
    },
    {
      key: 'sku_name',
      label: 'SKU Name',
      type: 'select',
      required: true,
      default: 'Standard_v2',
      options: APP_GATEWAY_SKUS,
    },
    {
      key: 'sku_tier',
      label: 'SKU Tier',
      type: 'select',
      required: true,
      default: 'Standard_v2',
      options: APP_GATEWAY_TIERS,
    },
    {
      key: 'sku_capacity',
      label: 'Capacity',
      type: 'select',
      required: true,
      default: 2,
      options: APP_GATEWAY_CAPACITY.map(String),
    },
  ],
  relationshipSuggestions: {
    inbound: ['azurerm_virtual_network', 'azurerm_subnet'],
    outbound: ['azurerm_public_ip', 'azurerm_app_service'],
  },
},

'azurerm_bastion_host': {
  displayName: 'Bastion Host',
  category: 'Network',
  description: 'Accès RDP/SSH sécurisé via le portail Azure',
  fields: [
    {
      key: 'name',
      label: 'Nom',
      type: 'text',
      required: true,
      placeholder: 'bastion-host',
    },
    {
      key: 'resource_group_name',
      label: 'Resource Group',
      type: 'text',
      required: true,
    },
    {
      key: 'sku',
      label: 'SKU',
      type: 'select',
      required: false,
      default: 'Basic',
      options: BASTION_HOST_SKUS,
    },
  ],
  relationshipSuggestions: {
    inbound: ['azurerm_subnet'],
    outbound: ['azurerm_public_ip'],
  },
},

'azurerm_express_route_circuit': {
  displayName: 'ExpressRoute Circuit',
  category: 'Network',
  description: 'Connexion privée dédiée vers Azure',
  fields: [
    {
      key: 'name',
      label: 'Nom',
      type: 'text',
      required: true,
      placeholder: 'er-onprem',
    },
    {
      key: 'resource_group_name',
      label: 'Resource Group',
      type: 'text',
      required: true,
    },
    {
      key: 'service_provider_name',
      label: 'Service Provider',
      type: 'text',
      required: true,
    },
    {
      key: 'peering_location',
      label: 'Peering Location',
      type: 'text',
      required: true,
    },
    {
      key: 'bandwidth_in_mbps',
      label: 'Bandwidth (Mbps)',
      type: 'select',
      required: true,
      default: 50,
      options: EXPRESS_ROUTE_BANDWIDTHS.map(String),
    },
    {
      key: 'sku_tier',
      label: 'Tier',
      type: 'select',
      required: true,
      default: 'Standard',
      options: EXPRESS_ROUTE_TIERS,
    },
    {
      key: 'sku_family',
      label: 'Family',
      type: 'select',
      required: true,
      default: 'MeteredData',
      options: EXPRESS_ROUTE_FAMILIES,
    },
  ],
  relationshipSuggestions: {
    inbound: [],
    outbound: ['azurerm_virtual_network'],
  },
},

'azurerm_dns_zone': {
  displayName: 'DNS Zone',
  category: 'Network',
  description: 'Zone DNS pour gérer les enregistrements DNS',
  fields: [
    {
      key: 'name',
      label: 'Domain Name',
      type: 'text',
      required: true,
      placeholder: 'example.com',
    },
    {
      key: 'resource_group_name',
      label: 'Resource Group',
      type: 'text',
      required: true,
    },
  ],
  relationshipSuggestions: {
    inbound: [],
    outbound: ['azurerm_public_ip', 'azurerm_app_service'],
  },
},

'azurerm_network_interface': {
  displayName: 'Network Interface',
  category: 'Network',
  description: 'Interface réseau pour les machines virtuelles',
  fields: [
    {
      key: 'name',
      label: 'Nom',
      type: 'text',
      required: true,
      placeholder: 'nic-vm01',
    },
    {
      key: 'resource_group_name',
      label: 'Resource Group',
      type: 'text',
      required: true,
    },
  ],
  relationshipSuggestions: {
    inbound: ['azurerm_subnet'],
    outbound: ['azurerm_virtual_machine', 'azurerm_linux_virtual_machine'],
  },
},

'azurerm_frontdoor': {
  displayName: 'Front Door',
  category: 'Network',
  description: 'CDN et équilibreur de charge global',
  fields: [
    {
      key: 'name',
      label: 'Nom',
      type: 'text',
      required: true,
      placeholder: 'fd-webapp',
    },
    {
      key: 'resource_group_name',
      label: 'Resource Group',
      type: 'text',
      required: true,
    },
  ],
  relationshipSuggestions: {
    inbound: [],
    outbound: ['azurerm_app_service', 'azurerm_storage_account'],
  },
},

'azurerm_cdn_frontdoor_profile': {
  displayName: 'CDN Front Door Profile',
  category: 'Network',
  description: 'Profil Front Door pour Azure CDN',
  fields: [
    {
      key: 'name',
      label: 'Nom',
      type: 'text',
      required: true,
      placeholder: 'fd-profile',
    },
    {
      key: 'resource_group_name',
      label: 'Resource Group',
      type: 'text',
      required: true,
    },
    {
      key: 'sku_name',
      label: 'SKU',
      type: 'select',
      required: true,
      default: 'Standard_AzureFrontDoor',
      options: FRONTDOOR_SKUS,
    },
  ],
  relationshipSuggestions: {
    inbound: [],
    outbound: ['azurerm_storage_account', 'azurerm_app_service'],
  },
},

'azurerm_cdn_profile': {
  displayName: 'CDN Profile',
  category: 'Network',
  description: 'Profil CDN pour la distribution de contenu',
  fields: [
    {
      key: 'name',
      label: 'Nom',
      type: 'text',
      required: true,
      placeholder: 'cdn-profile',
    },
    {
      key: 'resource_group_name',
      label: 'Resource Group',
      type: 'text',
      required: true,
    },
    {
      key: 'sku',
      label: 'SKU',
      type: 'select',
      required: true,
      default: 'Standard_Microsoft',
      options: CDN_SKUS,
    },
  ],
  relationshipSuggestions: {
    inbound: [],
    outbound: ['azurerm_cdn_endpoint'],
  },
},

'azurerm_cdn_endpoint': {
  displayName: 'CDN Endpoint',
  category: 'Network',
  description: 'Point de terminaison CDN',
  fields: [
    {
      key: 'name',
      label: 'Nom',
      type: 'text',
      required: true,
      placeholder: 'cdn-endpoint',
    },
    {
      key: 'resource_group_name',
      label: 'Resource Group',
      type: 'text',
      required: true,
    },
    {
      key: 'profile_name',
      label: 'CDN Profile Name',
      type: 'text',
      required: true,
    },
  ],
  relationshipSuggestions: {
    inbound: ['azurerm_cdn_profile'],
    outbound: ['azurerm_storage_account'],
  },
},

// ==================== STORAGE ====================

'azurerm_managed_disk': {
  displayName: 'Managed Disk',
  category: 'Storage',
  description: 'Disque géré pour machines virtuelles',
  fields: [
    {
      key: 'name',
      label: 'Nom',
      type: 'text',
      required: true,
      placeholder: 'disk-data01',
    },
    {
      key: 'resource_group_name',
      label: 'Resource Group',
      type: 'text',
      required: true,
    },
    {
      key: 'storage_account_type',
      label: 'Disk Type',
      type: 'select',
      required: true,
      default: 'Premium_LRS',
      options: DISK_TYPES,
    },
    {
      key: 'create_option',
      label: 'Create Option',
      type: 'select',
      required: true,
      default: 'Empty',
      options: DISK_CREATE_OPTIONS,
    },
    {
      key: 'disk_size_gb',
      label: 'Size (GB)',
      type: 'select',
      required: true,
      default: 128,
      options: DISK_SIZES.map(String),
    },
  ],
  relationshipSuggestions: {
    inbound: [],
    outbound: ['azurerm_virtual_machine', 'azurerm_linux_virtual_machine'],
  },
},

'azurerm_storage_blob': {
  displayName: 'Storage Blob',
  category: 'Storage',
  description: 'Blob dans un compte de stockage',
  fields: [
    {
      key: 'name',
      label: 'Nom',
      type: 'text',
      required: true,
      placeholder: 'myblob.txt',
    },
    {
      key: 'storage_account_name',
      label: 'Storage Account',
      type: 'text',
      required: true,
    },
    {
      key: 'storage_container_name',
      label: 'Container Name',
      type: 'text',
      required: true,
    },
    {
      key: 'type',
      label: 'Blob Type',
      type: 'select',
      required: true,
      default: 'Block',
      options: ['Block', 'Page', 'Append'],
    },
  ],
  relationshipSuggestions: {
    inbound: ['azurerm_storage_account'],
    outbound: [],
  },
},

'azurerm_data_lake_store_account': {
  displayName: 'Data Lake Store',
  category: 'Storage',
  description: 'Data Lake Storage Gen1',
  fields: [
    {
      key: 'name',
      label: 'Nom',
      type: 'text',
      required: true,
      placeholder: 'datalake',
    },
    {
      key: 'resource_group_name',
      label: 'Resource Group',
      type: 'text',
      required: true,
    },
  ],
  relationshipSuggestions: {
    inbound: [],
    outbound: ['azurerm_data_factory'],
  },
},

'azurerm_data_lake_analytics_account': {
  displayName: 'Data Lake Analytics',
  category: 'Analytics',
  description: 'Service d\'analyse Data Lake',
  fields: [
    {
      key: 'name',
      label: 'Nom',
      type: 'text',
      required: true,
      placeholder: 'dla-analytics',
    },
    {
      key: 'resource_group_name',
      label: 'Resource Group',
      type: 'text',
      required: true,
    },
    {
      key: 'default_store_account_name',
      label: 'Default Store Account',
      type: 'text',
      required: true,
    },
  ],
  relationshipSuggestions: {
    inbound: ['azurerm_data_lake_store_account'],
    outbound: [],
  },
},

// ==================== ANALYTICS & AI ====================

'azurerm_databricks_workspace': {
  displayName: 'Databricks Workspace',
  category: 'Analytics',
  description: 'Espace de travail Apache Spark géré',
  fields: [
    {
      key: 'name',
      label: 'Nom',
      type: 'text',
      required: true,
      placeholder: 'dbw-analytics',
    },
    {
      key: 'resource_group_name',
      label: 'Resource Group',
      type: 'text',
      required: true,
    },
    {
      key: 'sku',
      label: 'SKU',
      type: 'select',
      required: true,
      default: 'standard',
      options: DATABRICKS_SKUS,
    },
  ],
  relationshipSuggestions: {
    inbound: ['azurerm_virtual_network'],
    outbound: ['azurerm_storage_account', 'azurerm_data_lake_store_account'],
  },
},

'azurerm_synapse_workspace': {
  displayName: 'Synapse Workspace',
  category: 'Analytics',
  description: 'Espace de travail Azure Synapse Analytics',
  fields: [
    {
      key: 'name',
      label: 'Nom',
      type: 'text',
      required: true,
      placeholder: 'synapse-workspace',
    },
    {
      key: 'resource_group_name',
      label: 'Resource Group',
      type: 'text',
      required: true,
    },
    {
      key: 'storage_data_lake_gen2_filesystem_id',
      label: 'Data Lake Gen2 ID',
      type: 'text',
      required: true,
    },
  ],
  relationshipSuggestions: {
    inbound: ['azurerm_storage_account'],
    outbound: [],
  },
},

'azurerm_stream_analytics_job': {
  displayName: 'Stream Analytics Job',
  category: 'Analytics',
  description: 'Traitement de flux en temps réel',
  fields: [
    {
      key: 'name',
      label: 'Nom',
      type: 'text',
      required: true,
      placeholder: 'asa-realtime',
    },
    {
      key: 'resource_group_name',
      label: 'Resource Group',
      type: 'text',
      required: true,
    },
    {
      key: 'sku',
      label: 'SKU',
      type: 'select',
      required: false,
      default: 'Standard',
      options: STREAM_ANALYTICS_SKUS,
    },
    {
      key: 'compatibility_level',
      label: 'Compatibility Level',
      type: 'select',
      required: true,
      default: '1.2',
      options: STREAM_ANALYTICS_COMPATIBILITY_LEVELS,
    },
    {
      key: 'streaming_units',
      label: 'Streaming Units',
      type: 'select',
      required: true,
      default: 3,
      options: ['1', '3', '6', '12', '18', '24', '30', '36', '42', '48'],
    },
  ],
  relationshipSuggestions: {
    inbound: ['azurerm_eventhub_namespace', 'azurerm_iothub'],
    outbound: ['azurerm_storage_account', 'azurerm_sql_database'],
  },
},

'azurerm_data_factory': {
  displayName: 'Data Factory',
  category: 'Analytics',
  description: 'Service d\'intégration de données ETL',
  fields: [
    {
      key: 'name',
      label: 'Nom',
      type: 'text',
      required: true,
      placeholder: 'adf-etl',
    },
    {
      key: 'resource_group_name',
      label: 'Resource Group',
      type: 'text',
      required: true,
    },
  ],
  relationshipSuggestions: {
    inbound: [],
    outbound: ['azurerm_storage_account', 'azurerm_sql_database', 'azurerm_synapse_workspace'],
  },
},

'azurerm_machine_learning_workspace': {
  displayName: 'Machine Learning Workspace',
  category: 'AI + Machine Learning',
  description: 'Espace de travail Azure Machine Learning',
  fields: [
    {
      key: 'name',
      label: 'Nom',
      type: 'text',
      required: true,
      placeholder: 'mlw-models',
    },
    {
      key: 'resource_group_name',
      label: 'Resource Group',
      type: 'text',
      required: true,
    },
    {
      key: 'application_insights_id',
      label: 'Application Insights ID',
      type: 'text',
      required: true,
    },
    {
      key: 'key_vault_id',
      label: 'Key Vault ID',
      type: 'text',
      required: true,
    },
    {
      key: 'storage_account_id',
      label: 'Storage Account ID',
      type: 'text',
      required: true,
    },
  ],
  relationshipSuggestions: {
    inbound: ['azurerm_storage_account', 'azurerm_key_vault', 'azurerm_application_insights'],
    outbound: [],
  },
},

'azurerm_cognitive_account': {
  displayName: 'Cognitive Services',
  category: 'AI + Machine Learning',
  description: 'Services cognitifs Azure (Vision, NLP, etc.)',
  fields: [
    {
      key: 'name',
      label: 'Nom',
      type: 'text',
      required: true,
      placeholder: 'cog-vision',
    },
    {
      key: 'resource_group_name',
      label: 'Resource Group',
      type: 'text',
      required: true,
    },
    {
      key: 'kind',
      label: 'Service Kind',
      type: 'select',
      required: true,
      default: 'ComputerVision',
      options: COGNITIVE_SERVICES_KINDS,
    },
    {
      key: 'sku_name',
      label: 'SKU',
      type: 'select',
      required: true,
      default: 'S0',
      options: COGNITIVE_SERVICES_SKUS,
    },
  ],
  relationshipSuggestions: {
    inbound: [],
    outbound: ['azurerm_storage_account'],
  },
},

'azurerm_kusto_cluster': {
  displayName: 'Data Explorer Cluster',
  category: 'Analytics',
  description: 'Azure Data Explorer (Kusto) pour l\'analyse de données',
  fields: [
    {
      key: 'name',
      label: 'Nom',
      type: 'text',
      required: true,
      placeholder: 'adx-telemetry',
    },
    {
      key: 'resource_group_name',
      label: 'Resource Group',
      type: 'text',
      required: true,
    },
    {
      key: 'sku_name',
      label: 'SKU Name',
      type: 'select',
      required: true,
      default: 'Dev(No SLA)_Standard_D11_v2',
      options: KUSTO_SKUS,
    },
  ],
  relationshipSuggestions: {
    inbound: [],
    outbound: ['azurerm_storage_account', 'azurerm_eventhub_namespace'],
  },
},

// ==================== INTEGRATION ====================

'azurerm_logic_app_workflow': {
  displayName: 'Logic App',
  category: 'Integration',
  description: 'Automatisation de workflow',
  fields: [
    {
      key: 'name',
      label: 'Nom',
      type: 'text',
      required: true,
      placeholder: 'logic-workflow',
    },
    {
      key: 'resource_group_name',
      label: 'Resource Group',
      type: 'text',
      required: true,
    },
  ],
  relationshipSuggestions: {
    inbound: [],
    outbound: ['azurerm_storage_account', 'azurerm_eventhub_namespace', 'azurerm_service_bus_namespace'],
  },
},

'azurerm_logic_app_standard': {
  displayName: 'Logic App Standard',
  category: 'Integration',
  description: 'Logic App basé sur Azure Functions',
  fields: [
    {
      key: 'name',
      label: 'Nom',
      type: 'text',
      required: true,
      placeholder: 'logic-standard',
    },
    {
      key: 'resource_group_name',
      label: 'Resource Group',
      type: 'text',
      required: true,
    },
    {
      key: 'app_service_plan_id',
      label: 'App Service Plan ID',
      type: 'text',
      required: true,
    },
    {
      key: 'storage_account_name',
      label: 'Storage Account Name',
      type: 'text',
      required: true,
    },
  ],
  relationshipSuggestions: {
    inbound: ['azurerm_app_service_plan', 'azurerm_storage_account'],
    outbound: [],
  },
},

'azurerm_eventgrid_topic': {
  displayName: 'Event Grid Topic',
  category: 'Integration',
  description: 'Rubrique Event Grid pour pub/sub',
  fields: [
    {
      key: 'name',
      label: 'Nom',
      type: 'text',
      required: true,
      placeholder: 'egt-events',
    },
    {
      key: 'resource_group_name',
      label: 'Resource Group',
      type: 'text',
      required: true,
    },
    {
      key: 'input_schema',
      label: 'Input Schema',
      type: 'select',
      required: false,
      default: 'EventGridSchema',
      options: EVENT_GRID_TOPIC_INPUT_SCHEMAS,
    },
  ],
  relationshipSuggestions: {
    inbound: [],
    outbound: ['azurerm_eventgrid_event_subscription'],
  },
},

'azurerm_eventgrid_domain': {
  displayName: 'Event Grid Domain',
  category: 'Integration',
  description: 'Domaine Event Grid pour organiser les topics',
  fields: [
    {
      key: 'name',
      label: 'Nom',
      type: 'text',
      required: true,
      placeholder: 'egd-domain',
    },
    {
      key: 'resource_group_name',
      label: 'Resource Group',
      type: 'text',
      required: true,
    },
  ],
  relationshipSuggestions: {
    inbound: [],
    outbound: ['azurerm_eventgrid_topic'],
  },
},

'azurerm_eventgrid_event_subscription': {
  displayName: 'Event Grid Subscription',
  category: 'Integration',
  description: 'Abonnement Event Grid',
  fields: [
    {
      key: 'name',
      label: 'Nom',
      type: 'text',
      required: true,
      placeholder: 'egs-subscription',
    },
    {
      key: 'scope',
      label: 'Scope (Resource ID)',
      type: 'text',
      required: true,
    },
  ],
  relationshipSuggestions: {
    inbound: ['azurerm_eventgrid_topic', 'azurerm_storage_account'],
    outbound: ['azurerm_function_app', 'azurerm_logic_app_workflow'],
  },
},

'azurerm_iothub': {
  displayName: 'IoT Hub',
  category: 'Internet of Things',
  description: 'Hub IoT pour connecter des appareils',
  fields: [
    {
      key: 'name',
      label: 'Nom',
      type: 'text',
      required: true,
      placeholder: 'iothub-devices',
    },
    {
      key: 'resource_group_name',
      label: 'Resource Group',
      type: 'text',
      required: true,
    },
    {
      key: 'sku_name',
      label: 'SKU Name',
      type: 'select',
      required: true,
      default: 'S1',
      options: IOT_HUB_SKUS,
    },
    {
      key: 'sku_capacity',
      label: 'SKU Capacity',
      type: 'number',
      required: true,
      default: 1,
    },
  ],
  relationshipSuggestions: {
    inbound: [],
    outbound: ['azurerm_storage_account', 'azurerm_eventhub_namespace', 'azurerm_stream_analytics_job'],
  },
},

'azurerm_iotcentral_application': {
  displayName: 'IoT Central Application',
  category: 'Internet of Things',
  description: 'Application IoT Central SaaS',
  fields: [
    {
      key: 'name',
      label: 'Nom',
      type: 'text',
      required: true,
      placeholder: 'iotc-app',
    },
    {
      key: 'resource_group_name',
      label: 'Resource Group',
      type: 'text',
      required: true,
    },
    {
      key: 'sub_domain',
      label: 'Subdomain',
      type: 'text',
      required: true,
      placeholder: 'myapp',
    },
    {
      key: 'sku',
      label: 'SKU',
      type: 'select',
      required: false,
      default: 'ST2',
      options: ['ST0', 'ST1', 'ST2'],
    },
  ],
  relationshipSuggestions: {
    inbound: [],
    outbound: [],
  },
},

'azurerm_signalr_service': {
  displayName: 'SignalR Service',
  category: 'Integration',
  description: 'Service de messagerie en temps réel',
  fields: [
    {
      key: 'name',
      label: 'Nom',
      type: 'text',
      required: true,
      placeholder: 'signalr-chat',
    },
    {
      key: 'resource_group_name',
      label: 'Resource Group',
      type: 'text',
      required: true,
    },
    {
      key: 'sku_name',
      label: 'SKU',
      type: 'select',
      required: true,
      default: 'Standard_S1',
      options: SIGNALR_SERVICE_SKUS,
    },
    {
      key: 'sku_capacity',
      label: 'Capacity',
      type: 'select',
      required: true,
      default: 1,
      options: ['1', '2', '5', '10', '20', '50', '100'],
    },
  ],
  relationshipSuggestions: {
    inbound: [],
    outbound: ['azurerm_app_service', 'azurerm_function_app'],
  },
},

'azurerm_servicebus_namespace': {
  displayName: 'Service Bus Namespace',
  category: 'Integration',
  description: 'Namespace Service Bus (différent de service_bus_namespace)',
  fields: [
    {
      key: 'name',
      label: 'Nom',
      type: 'text',
      required: true,
      placeholder: 'sb-namespace',
    },
    {
      key: 'resource_group_name',
      label: 'Resource Group',
      type: 'text',
      required: true,
    },
    {
      key: 'sku',
      label: 'SKU',
      type: 'select',
      required: true,
      default: 'Standard',
      options: SERVICE_BUS_SKUS,
    },
  ],
  relationshipSuggestions: {
    inbound: [],
    outbound: ['azurerm_function_app', 'azurerm_logic_app_workflow'],
  },
},

// ==================== MANAGEMENT & SECURITY ====================

'azurerm_api_management': {
  displayName: 'API Management',
  category: 'Integration',
  description: 'Gateway API avec gestion et sécurisation',
  fields: [
    {
      key: 'name',
      label: 'Nom',
      type: 'text',
      required: true,
      placeholder: 'apim-gateway',
    },
    {
      key: 'resource_group_name',
      label: 'Resource Group',
      type: 'text',
      required: true,
    },
    {
      key: 'publisher_name',
      label: 'Publisher Name',
      type: 'text',
      required: true,
      placeholder: 'My Company',
    },
    {
      key: 'publisher_email',
      label: 'Publisher Email',
      type: 'text',
      required: true,
      placeholder: 'admin@example.com',
    },
    {
      key: 'sku_name',
      label: 'SKU',
      type: 'select',
      required: true,
      default: 'Developer_1',
      options: ['Consumption_0', 'Developer_1', 'Basic_1', 'Standard_1', 'Premium_1'],
    },
  ],
  relationshipSuggestions: {
    inbound: ['azurerm_virtual_network'],
    outbound: ['azurerm_app_service', 'azurerm_function_app'],
  },
},

'azurerm_key_vault_secret': {
  displayName: 'Key Vault Secret',
  category: 'Security',
  description: 'Secret stocké dans Key Vault',
  fields: [
    {
      key: 'name',
      label: 'Nom',
      type: 'text',
      required: true,
      placeholder: 'db-password',
    },
    {
      key: 'key_vault_id',
      label: 'Key Vault ID',
      type: 'text',
      required: true,
    },
    {
      key: 'value',
      label: 'Value',
      type: 'text',
      required: true,
    },
  ],
  relationshipSuggestions: {
    inbound: ['azurerm_key_vault'],
    outbound: [],
  },
},

'azurerm_key_vault_key': {
  displayName: 'Key Vault Key',
  category: 'Security',
  description: 'Clé cryptographique dans Key Vault',
  fields: [
    {
      key: 'name',
      label: 'Nom',
      type: 'text',
      required: true,
      placeholder: 'encryption-key',
    },
    {
      key: 'key_vault_id',
      label: 'Key Vault ID',
      type: 'text',
      required: true,
    },
    {
      key: 'key_type',
      label: 'Key Type',
      type: 'select',
      required: true,
      default: 'RSA',
      options: ['EC', 'RSA', 'RSA-HSM', 'EC-HSM'],
    },
    {
      key: 'key_size',
      label: 'Key Size',
      type: 'select',
      required: false,
      default: 2048,
      options: ['2048', '3072', '4096'],
    },
  ],
  relationshipSuggestions: {
    inbound: ['azurerm_key_vault'],
    outbound: [],
  },
},

'azurerm_key_vault_certificate': {
  displayName: 'Key Vault Certificate',
  category: 'Security',
  description: 'Certificat stocké dans Key Vault',
  fields: [
    {
      key: 'name',
      label: 'Nom',
      type: 'text',
      required: true,
      placeholder: 'ssl-cert',
    },
    {
      key: 'key_vault_id',
      label: 'Key Vault ID',
      type: 'text',
      required: true,
    },
  ],
  relationshipSuggestions: {
    inbound: ['azurerm_key_vault'],
    outbound: ['azurerm_application_gateway', 'azurerm_app_service'],
  },
},

'azurerm_user_assigned_identity': {
  displayName: 'User Assigned Identity',
  category: 'Security',
  description: 'Identité managée assignée par l\'utilisateur',
  fields: [
    {
      key: 'name',
      label: 'Nom',
      type: 'text',
      required: true,
      placeholder: 'id-appservice',
    },
    {
      key: 'resource_group_name',
      label: 'Resource Group',
      type: 'text',
      required: true,
    },
  ],
  relationshipSuggestions: {
    inbound: [],
    outbound: ['azurerm_app_service', 'azurerm_function_app', 'azurerm_virtual_machine'],
  },
},

'azurerm_role_assignment': {
  displayName: 'Role Assignment',
  category: 'Security',
  description: 'Attribution de rôle RBAC',
  fields: [
    {
      key: 'scope',
      label: 'Scope (Resource ID)',
      type: 'text',
      required: true,
    },
    {
      key: 'role_definition_name',
      label: 'Role Name',
      type: 'select',
      required: false,
      options: ['Owner', 'Contributor', 'Reader', 'User Access Administrator'],
    },
    {
      key: 'principal_id',
      label: 'Principal ID',
      type: 'text',
      required: true,
    },
  ],
  relationshipSuggestions: {
    inbound: [],
    outbound: [],
  },
},

'azurerm_policy_assignment': {
  displayName: 'Policy Assignment',
  category: 'Management',
  description: 'Attribution de politique Azure',
  fields: [
    {
      key: 'name',
      label: 'Nom',
      type: 'text',
      required: true,
      placeholder: 'policy-enforce-tags',
    },
    {
      key: 'scope',
      label: 'Scope (Resource ID)',
      type: 'text',
      required: true,
    },
    {
      key: 'policy_definition_id',
      label: 'Policy Definition ID',
      type: 'text',
      required: true,
    },
  ],
  relationshipSuggestions: {
    inbound: [],
    outbound: [],
  },
},

'azurerm_monitor_action_group': {
  displayName: 'Monitor Action Group',
  category: 'Management',
  description: 'Groupe d\'actions pour les alertes',
  fields: [
    {
      key: 'name',
      label: 'Nom',
      type: 'text',
      required: true,
      placeholder: 'ag-alerts',
    },
    {
      key: 'resource_group_name',
      label: 'Resource Group',
      type: 'text',
      required: true,
    },
    {
      key: 'short_name',
      label: 'Short Name',
      type: 'text',
      required: true,
      placeholder: 'alerts',
    },
  ],
  relationshipSuggestions: {
    inbound: [],
    outbound: [],
  },
},

'azurerm_application_insights': {
  displayName: 'Application Insights',
  category: 'Management',
  description: 'Monitoring et télémétrie des applications',
  fields: [
    {
      key: 'name',
      label: 'Nom',
      type: 'text',
      required: true,
      placeholder: 'ai-webapp',
    },
    {
      key: 'resource_group_name',
      label: 'Resource Group',
      type: 'text',
      required: true,
    },
    {
      key: 'application_type',
      label: 'Application Type',
      type: 'select',
      required: true,
      default: 'web',
      options: APPLICATION_INSIGHTS_TYPES,
    },
  ],
  relationshipSuggestions: {
    inbound: [],
    outbound: ['azurerm_app_service', 'azurerm_function_app'],
  },
},

'azurerm_recovery_services_vault': {
  displayName: 'Recovery Services Vault',
  category: 'Management',
  description: 'Coffre pour la sauvegarde et la récupération',
  fields: [
    {
      key: 'name',
      label: 'Nom',
      type: 'text',
      required: true,
      placeholder: 'rsv-backup',
    },
    {
      key: 'resource_group_name',
      label: 'Resource Group',
      type: 'text',
      required: true,
    },
    {
      key: 'sku',
      label: 'SKU',
      type: 'select',
      required: true,
      default: 'Standard',
      options: RECOVERY_SERVICES_VAULT_SKUS,
    },
  ],
  relationshipSuggestions: {
    inbound: [],
    outbound: ['azurerm_virtual_machine', 'azurerm_storage_account'],
  },
},

'azurerm_automation_account': {
  displayName: 'Automation Account',
  category: 'Management',
  description: 'Compte d\'automatisation pour runbooks',
  fields: [
    {
      key: 'name',
      label: 'Nom',
      type: 'text',
      required: true,
      placeholder: 'aa-automation',
    },
    {
      key: 'resource_group_name',
      label: 'Resource Group',
      type: 'text',
      required: true,
    },
    {
      key: 'sku_name',
      label: 'SKU',
      type: 'select',
      required: true,
      default: 'Basic',
      options: AUTOMATION_ACCOUNT_SKUS,
    },
  ],
  relationshipSuggestions: {
    inbound: [],
    outbound: [],
  },
},

'azurerm_search_service': {
  displayName: 'Cognitive Search',
  category: 'AI + Machine Learning',
  description: 'Service de recherche Azure Cognitive Search',
  fields: [
    {
      key: 'name',
      label: 'Nom',
      type: 'text',
      required: true,
      placeholder: 'search-service',
    },
    {
      key: 'resource_group_name',
      label: 'Resource Group',
      type: 'text',
      required: true,
    },
    {
      key: 'sku',
      label: 'SKU',
      type: 'select',
      required: true,
      default: 'standard',
      options: SEARCH_SERVICE_SKUS,
    },
  ],
  relationshipSuggestions: {
    inbound: [],
    outbound: ['azurerm_storage_account', 'azurerm_cognitive_account'],
  },
},

'azurerm_bot_service': {
  displayName: 'Bot Service',
  category: 'AI + Machine Learning',
  description: 'Service Azure Bot',
  fields: [
    {
      key: 'name',
      label: 'Nom',
      type: 'text',
      required: true,
      placeholder: 'bot-chatbot',
    },
    {
      key: 'resource_group_name',
      label: 'Resource Group',
      type: 'text',
      required: true,
    },
    {
      key: 'sku',
      label: 'SKU',
      type: 'select',
      required: true,
      default: 'S1',
      options: BOT_SERVICE_SKUS,
    },
    {
      key: 'microsoft_app_id',
      label: 'Microsoft App ID',
      type: 'text',
      required: true,
    },
  ],
  relationshipSuggestions: {
    inbound: [],
    outbound: ['azurerm_app_service', 'azurerm_cognitive_account'],
  },
},

'azurerm_media_services_account': {
  displayName: 'Media Services',
  category: 'Media',
  description: 'Compte Azure Media Services',
  fields: [
    {
      key: 'name',
      label: 'Nom',
      type: 'text',
      required: true,
      placeholder: 'ams-streaming',
    },
    {
      key: 'resource_group_name',
      label: 'Resource Group',
      type: 'text',
      required: true,
    },
    {
      key: 'storage_account_id',
      label: 'Storage Account ID',
      type: 'text',
      required: true,
    },
  ],
  relationshipSuggestions: {
    inbound: ['azurerm_storage_account'],
    outbound: [],
  },
},

'azurerm_cosmosdb_sql_database': {
  displayName: 'Cosmos DB SQL Database',
  category: 'Database',
  description: 'Base de données SQL dans Cosmos DB',
  fields: [
    {
      key: 'name',
      label: 'Nom',
      type: 'text',
      required: true,
      placeholder: 'cosmosdb-database',
    },
    {
      key: 'resource_group_name',
      label: 'Resource Group',
      type: 'text',
      required: true,
    },
    {
      key: 'account_name',
      label: 'Cosmos Account Name',
      type: 'text',
      required: true,
    },
    {
      key: 'throughput',
      label: 'Throughput (RU/s)',
      type: 'select',
      required: false,
      options: ['400', '1000', '4000', '10000', '20000'],
    },
  ],
  relationshipSuggestions: {
    inbound: ['azurerm_cosmosdb_account'],
    outbound: [],
  },
},

'azurerm_sentinel_log_analytics_workspace_onboarding': {
  displayName: 'Azure Sentinel',
  category: 'Security',
  description: 'Microsoft Sentinel (SIEM)',
  fields: [
    {
      key: 'workspace_id',
      label: 'Log Analytics Workspace ID',
      type: 'text',
      required: true,
    },
  ],
  relationshipSuggestions: {
    inbound: [],
    outbound: [],
  },
},

'azurerm_service_fabric_cluster': {
  displayName: 'Service Fabric Cluster',
  category: 'Compute',
  description: 'Cluster Service Fabric pour microservices',
  fields: [
    {
      key: 'name',
      label: 'Nom',
      type: 'text',
      required: true,
      placeholder: 'sf-cluster',
    },
    {
      key: 'resource_group_name',
      label: 'Resource Group',
      type: 'text',
      required: true,
    },
    {
      key: 'reliability_level',
      label: 'Reliability Level',
      type: 'select',
      required: true,
      default: 'Bronze',
      options: SERVICE_FABRIC_RELIABILITY_LEVELS,
    },
    {
      key: 'vm_image',
      label: 'VM Image',
      type: 'text',
      required: true,
      default: 'Windows',
    },
  ],
  relationshipSuggestions: {
    inbound: ['azurerm_virtual_network', 'azurerm_lb'],
    outbound: ['azurerm_storage_account'],
  },
},

'azurerm_service_fabric_mesh_application': {
  displayName: 'Service Fabric Mesh',
  category: 'Compute',
  description: 'Application Service Fabric Mesh serverless',
  fields: [
    {
      key: 'name',
      label: 'Nom',
      type: 'text',
      required: true,
      placeholder: 'sfm-app',
    },
    {
      key: 'resource_group_name',
      label: 'Resource Group',
      type: 'text',
      required: true,
    },
  ],
  relationshipSuggestions: {
    inbound: [],
    outbound: ['azurerm_container_registry'],
  },
},

'azurerm_site_recovery_fabric': {
  displayName: 'Site Recovery Fabric',
  category: 'Management',
  description: 'Fabric pour la récupération de site',
  fields: [
    {
      key: 'name',
      label: 'Nom',
      type: 'text',
      required: true,
      placeholder: 'asr-fabric',
    },
    {
      key: 'resource_group_name',
      label: 'Resource Group',
      type: 'text',
      required: true,
    },
    {
      key: 'recovery_vault_name',
      label: 'Recovery Vault Name',
      type: 'text',
      required: true,
    },
  ],
  relationshipSuggestions: {
    inbound: ['azurerm_recovery_services_vault'],
    outbound: [],
  },
},

'azurerm_app_configuration': {
  displayName: 'App Configuration',
  category: 'Management',
  description: 'Service de configuration centralisée',
  fields: [
    {
      key: 'name',
      label: 'Nom',
      type: 'text',
      required: true,
      placeholder: 'appconfig-settings',
    },
    {
      key: 'resource_group_name',
      label: 'Resource Group',
      type: 'text',
      required: true,
    },
    {
      key: 'sku',
      label: 'SKU',
      type: 'select',
      required: false,
      default: 'standard',
      options: ['free', 'standard'],
    },
  ],
  relationshipSuggestions: {
    inbound: [],
    outbound: ['azurerm_app_service', 'azurerm_function_app'],
  },
},

'azurerm_app_service_certificate': {
  displayName: 'App Service Certificate',
  category: 'Security',
  description: 'Certificat pour App Service',
  fields: [
    {
      key: 'name',
      label: 'Nom',
      type: 'text',
      required: true,
      placeholder: 'cert-webapp',
    },
    {
      key: 'resource_group_name',
      label: 'Resource Group',
      type: 'text',
      required: true,
    },
    {
      key: 'pfx_blob',
      label: 'PFX Certificate',
      type: 'text',
      required: true,
    },
  ],
  relationshipSuggestions: {
    inbound: [],
    outbound: ['azurerm_app_service'],
  },
},

'azurerm_app_service_environment': {
  displayName: 'App Service Environment',
  category: 'Compute',
  description: 'App Service Environment isolé',
  fields: [
    {
      key: 'name',
      label: 'Nom',
      type: 'text',
      required: true,
      placeholder: 'ase-isolated',
    },
    {
      key: 'resource_group_name',
      label: 'Resource Group',
      type: 'text',
      required: true,
    },
    {
      key: 'subnet_id',
      label: 'Subnet ID',
      type: 'text',
      required: true,
    },
  ],
  relationshipSuggestions: {
    inbound: ['azurerm_subnet'],
    outbound: ['azurerm_app_service'],
  },
},

'azurerm_arc_machine': {
  displayName: 'Arc-enabled Server',
  category: 'Hybrid + Multicloud',
  description: 'Serveur géré via Azure Arc',
  fields: [
    {
      key: 'name',
      label: 'Nom',
      type: 'text',
      required: true,
      placeholder: 'arc-server',
    },
    {
      key: 'resource_group_name',
      label: 'Resource Group',
      type: 'text',
      required: true,
    },
  ],
  relationshipSuggestions: {
    inbound: [],
    outbound: [],
  },
},

'azurerm_active_directory_domain_service': {
  displayName: 'Azure AD Domain Services',
  category: 'Security',
  description: 'Services de domaine Azure Active Directory',
  fields: [
    {
      key: 'name',
      label: 'Nom',
      type: 'text',
      required: true,
      placeholder: 'aadds-domain',
    },
    {
      key: 'resource_group_name',
      label: 'Resource Group',
      type: 'text',
      required: true,
    },
    {
      key: 'domain_name',
      label: 'Domain Name',
      type: 'text',
      required: true,
      placeholder: 'example.com',
    },
    {
      key: 'sku',
      label: 'SKU',
      type: 'select',
      required: true,
      default: 'Standard',
      options: ['Standard', 'Enterprise', 'Premium'],
    },
  ],
  relationshipSuggestions: {
    inbound: ['azurerm_virtual_network'],
    outbound: [],
  },
},
};

/**
 * Obtenir la configuration d'une ressource par son type Terraform
 */
export const getResourceConfig = (tfKey: string): ResourceConfig | null => {
  return AZURE_RESOURCE_CONFIG[tfKey] || null;
};

/**
 * Obtenir les suggestions pour un champ spécifique
 */
export const getFieldSuggestions = (tfKey: string, fieldKey: string): string[] => {
  const config = getResourceConfig(tfKey);
  if (!config) return [];

  const field = config.fields.find(f => f.key === fieldKey);
  return field?.suggestions || field?.options || [];
};
